import { appActions, appStore } from '@/core/state/app'
import { uiActions } from '@/core/state/ui'
import { networkDataStore } from '@/core/state/networkData'
import { optimizationAPI } from '@/core/api'
import { fetchNetworkData } from '@/core/cache/useQueries'
import { invalidateAfterOptimizationRun } from '@/core/cache/useCacheInvalidation'
// Aliased away from the `use` prefix: the React Hooks lint rule reads any
// top-level `useX()` call as a misplaced hook, and this is a Vue composable.
import { useGettingStarted as gettingStartedTracker } from '@/features/onboarding/composables/useGettingStarted'
import { requestMapRender } from './mapBridge'
import { setRenderableNetwork } from './filters'
import { defaultContext, type CommandContext } from './context'

/**
 * Running an optimization, as a plain function (Phase 1D).
 * `useOptimizationHandlers.js` is a shim over this until 1E replaces the
 * toolbar and the menu with React, which call it directly.
 */

const OPTIMIZATION_CONFIG_KEY = 'optimizationConfig'

const { markDone: markGettingStartedDone } = gettingStartedTracker()

export type RunOptimizationOptions = {
  /** Always false. Kept because the old signature took it and logged loudly if it was ever true. */
  autoSwitch?: boolean
  /**
   * Build the full network on the footprint the two-stage model chose.
   * `optimization_pipeline.py:671` reads this straight off the request body and
   * pins the stored design footprint. The Design-Under-Uncertainty block's
   * "build on this footprint" button has always asked for it and the option was
   * dropped on the floor — the handler took one parameter and the call passed
   * three — so the button ran an ordinary optimization.
   */
  pinToDesignFootprint?: boolean
}

type SavedConfig = {
  objective?: unknown
  facilityConfig?: { mode?: string }
  fixedN?: unknown
  multipliers?: unknown
  purchaseMethodOptimization?: unknown
}

/** The config the run should use: the backend's if it has one, else the last one saved locally. */
async function loadSavedConfig(): Promise<SavedConfig | null> {
  const state = appStore.getState()
  try {
    const response = await optimizationAPI.getOptimizationConfig(state.currentScenarioId || 1, state.workspaceId || 0)
    if (response && response.status === 'success' && response.config) return response.config as SavedConfig
  } catch (error) {
    console.warn('⚠️ [Optimization] Could not load config from backend, trying localStorage:', error)
  }
  try {
    const saved = localStorage.getItem(OPTIMIZATION_CONFIG_KEY)
    if (saved) return JSON.parse(saved) as SavedConfig
  } catch {
    /* a corrupt saved config is ignored, as before */
  }
  return null
}

/** The message a failed run should show, from the backend's structured error. */
export function optimizationErrorMessage(error: unknown): string {
  const response = (error as { response?: { data?: Record<string, string> } }).response?.data ?? {}
  switch (response.error_code) {
    case 'NO_SOURCES':
      return '⚠️ No source facilities found. Please define sources in your Excel import (Sources tab) or add supply chain paths in Configuration.'
    case 'NO_FLOW_VARS':
      return '⚠️ No valid flows could be created. Check that your Sources tab lists the correct products for each facility.'
    case 'SET_FLOW_CONFLICT':
      return '⚠️ Infeasible: Your lanes have set_flow values that lock flows to fixed quantities, which conflict with demand or capacity constraints. To optimize freely, remove or clear the set_flow column in your Lanes data and re-import.'
    default:
      if (response.diagnostic_message) return `Optimization failed: ${response.diagnostic_message}`
      return `Optimization failed: ${response.error || (error as Error)?.message}`
  }
}

export async function runOptimization(
  ctx: CommandContext = defaultContext(),
  options: RunOptimizationOptions = {},
): Promise<unknown> {
  const { autoSwitch = false, pinToDesignFootprint = false } = options

  // The gate is asked here rather than handed in. It used to be an optional
  // parameter because its state was refs created fresh inside every
  // `useFeatureGating()` call — so only the instance MapView destructured was
  // bound to the upgrade modal, and a command could not open it. That meant a
  // caller who simply omitted the parameter ran ungated. Phase 1F moved the
  // gate into the ui store, so there is one of it and no way to skip it.
  if (!uiActions.requireFeature('run_optimization', 'Run Scenario')) return undefined

  const { queryClient } = ctx
  try {
    uiActions.setOptimizing(true)
    appActions.setLoading(true, 'Running optimization...')

    const savedConfig = await loadSavedConfig()

    const effectiveWorkspaceId = appActions.validateWorkspaceId()
    const state = appStore.getState()
    const payload: Record<string, unknown> = {
      scenario_id: state.currentScenarioId || 1,
      use_baseline: true,
      workspace_id: effectiveWorkspaceId,
    }
    if (savedConfig) {
      payload.objective = savedConfig.objective
      payload.facilityConfig = savedConfig.facilityConfig
      payload.fixedN = savedConfig.fixedN
      payload.multipliers = savedConfig.multipliers
      if (savedConfig.purchaseMethodOptimization) payload.purchaseMethodOptimization = savedConfig.purchaseMethodOptimization
    }
    // The pipeline reads this off the request body. Only sent when asked for,
    // so an ordinary run is byte-for-byte the request it always was.
    if (pinToDesignFootprint) payload.pinToDesignFootprint = true

    const initialScenario = state.currentScenario
    const initialScenarioId = state.currentScenarioId

    const optimizationResult = await optimizationAPI.runOptimization(payload)

    const resultScenarioId = (payload.scenario_id as number) || appStore.getState().currentScenarioId || 1

    // The run's response is deliberately NOT written into the costs cache.
    //
    // It is a different shape from the one `getScenarioCosts` produces and every
    // reader of that key expects — no `service_level`, no `open_dcs`, no
    // `demand_fulfilled` — and `stats.ts` turns each missing field into a
    // displayed zero rather than an em dash. Writing it here is what made the
    // Service Level card read "0.0%" after a run that reported 100% fulfilment.
    // `invalidateAfterOptimizationRun` below refetches the key through
    // the normalizer instead.
    appActions.markOptimizationRun()
    appActions.setCurrentResultType('optimization')

    if (autoSwitch) {
      console.error('❌ [SECURITY] Auto-switch attempted! This should NEVER happen.')
    } else if (
      appStore.getState().currentScenarioId !== initialScenarioId ||
      appStore.getState().currentScenario !== initialScenario
    ) {
      console.error('❌ [TEST FAIL] Scenario changed during the run; restoring it')
      appActions.setCurrentScenario(initialScenario, initialScenarioId)
    }

    appActions.setLoading(true, 'Loading updated data...')
    await invalidateAfterOptimizationRun(queryClient, resultScenarioId)

    const freshNetworkData = await fetchNetworkData(resultScenarioId, 'optimized', appStore.getState().workspaceId)
    if (freshNetworkData && (freshNetworkData.facilities?.length > 0 || freshNetworkData.lanes?.length > 0)) {
      setRenderableNetwork(freshNetworkData)
      requestMapRender(freshNetworkData, networkDataStore.getState().filters)
    }

    appActions.showSnackbar('Optimization completed successfully!', 'success')

    // Ticks the getting-started row. Deliberately on the success path only — a
    // run that threw has not shown the user what an optimization does, and
    // marking it done would quietly remove the prompt to try again.
    markGettingStartedDone('run_optimization')

    return optimizationResult
  } catch (error) {
    console.error('❌ [Optimization] Error running optimization:', error)
    appActions.showSnackbar(optimizationErrorMessage(error), 'error', 15000)
    throw error
  } finally {
    uiActions.setOptimizing(false)
    appActions.setLoading(false)
  }
}
